import { ExecuteParameters, ExecuteResponse, FilterableModel, Model, NotificationId, VerbId } from '@tableau/api-internal-contract-js';
import { PresentationModel } from '@tableau/preslayer-api-contract';
import { NotificationId as PresLayerEventId } from '../../gen-src/preslayer/Events';
import { ApiUtils } from '../common-bootstrap/ApiUtils';
import { PresentationLayer } from '../common-bootstrap/NoOpPresentationLayer';
import { Disposable } from '../Disposable';
import { CommandMappingRegistry } from '../mappings/CommandMappingRegistry';
import { EmbeddingCommandMappingRegistryFactory } from '../mappings/EmbeddingCommandMappingRegistryFactory';
import { EventMappingRegistryFactory } from '../mappings/EmbeddingEventMappingRegistryFactory';
import {
  ApiToPresLayerMappingRegistry,
  ParameterMappingRegistryFactory,
  PresLayerToApiMappingRegistry,
} from '../mappings/ParameterMappingRegistryFactory';
import { EventMappingRegistry } from '../mappings/SharedEventMappingRegistryFactory';
import { telemetryFunctionComplete, telemetryNotificationComplete } from '../telemetry/EmbeddingTelemetryDispatcher';
import { FunctionResults, telemetryFunctionResponse, telemetryFunctionStart } from '../telemetry/TelemetryDispatcher';

export type NotificationHandler = (notification: NotificationId, model: Model) => void;

export class EmbeddingApiEventHandler implements Disposable {
  private _eventHandler?: NotificationHandler;
  private readonly _commandRegistry: CommandMappingRegistry;
  private readonly _eventRegistry: EventMappingRegistry;
  private readonly _apiToPresLayerRegistry: ApiToPresLayerMappingRegistry;
  private readonly _presLayerToApiRegistry: PresLayerToApiMappingRegistry;
  private readonly _notificationUnregisterFunctions: Array<() => void> = [];

  /**
   * Creates an instance of ApiEventHandler.
   * @param {PresentationLayer} presLayer - An implementation of the pres-layer Api to use
   * @param {AddInLocatorPresModel} addInLocatorPresModel - locator for the add-in which is being referenced
   * @param {runningInDesktop} boolean - true if this is ApiEventHandler is created in a Desktop context.
   * @memberof ApiEventHandler
   */
  public constructor(private _presLayer: PresentationLayer) {
    // Initialize all our registries
    this._commandRegistry = EmbeddingCommandMappingRegistryFactory.createCommandMappingRegistry();
    this._apiToPresLayerRegistry = ParameterMappingRegistryFactory.createApiToPresLayerParamRegistry();
    this._presLayerToApiRegistry = ParameterMappingRegistryFactory.createPresLayerToApiParamRegistry();
    this._eventRegistry = EventMappingRegistryFactory.createEventMappingRegistry();
    for (const plEventId of Object.keys(this._eventRegistry)) {
      // Register for all notifications we know about and bind the event type to the callback
      this._notificationUnregisterFunctions.push(
        this._presLayer.registerNotificationHandler(plEventId, this.onPresLayerNotification.bind(this, plEventId)),
      );
    }
  }

  public execute(verb: VerbId, parameters: ExecuteParameters): Promise<ExecuteResponse> {
    const command = this._commandRegistry.getCommand(verb);
    parameters = parameters || {};
    const commandsParams = command.createParams(parameters, this._apiToPresLayerRegistry);
    const runtimeData = telemetryFunctionStart();
    return this._presLayer
      .invokeCommand<ApiUtils.CommandResult>(command.namespace, command.commandId, commandsParams)
      .then<ExecuteResponse>((commandResult) => {
        telemetryFunctionResponse(runtimeData);
        const convertedResult = command.ProcessResult(commandResult, this._presLayerToApiRegistry);
        telemetryFunctionComplete(verb, runtimeData, parameters, FunctionResults.Success(convertedResult));
        return {
          result: convertedResult,
        };
      });
  }

  public setEventHandler(eventHandler: NotificationHandler): void {
    this._eventHandler = eventHandler;
  }

  /**
   * Called when a new event notification comes in from the presentation layer
   *
   * @param eventId The pres layer event id
   * @param presModel The pres model that is included with the event
   */
  private onPresLayerNotification(eventId: PresLayerEventId, presModel: PresentationModel): void {
    // First look up this eventId in our registry and convert to presModel
    const eventMapping = this._eventRegistry[eventId];
    const apiModel = eventMapping.converter(presModel) as FilterableModel;
    if (this._eventHandler) {
      const runtimeData = telemetryFunctionStart();
      const notificationID = eventMapping.apiId;
      this._eventHandler(notificationID, apiModel);
      telemetryNotificationComplete(notificationID, runtimeData, FunctionResults.Success(apiModel));
    }
  }

  public dispose(): void {
    for (const unregisterNotification of this._notificationUnregisterFunctions) {
      unregisterNotification();
    }
  }
}
